import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { ChevronLeftIcon, ChevronRightIcon, LockIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { toKey } from '../data/homework-data'
import { TASK_TYPE_CONFIG } from '../data/task-config'
import { weekdayHeaders } from '../data/types'
import { useHomework } from '../providers/homework-provider'

interface MonthCalendarProps {
  /** Jump the weekly view to the week containing this date */
  onSelectDay: (day: Date) => void
}

export function MonthCalendar({ onSelectDay }: MonthCalendarProps) {
  const {
    tasks,
    isWeekLocked,
    isCompleted,
    isCommentsRead,
    openTask,
    setFocusDate,
  } = useHomework()
  const [month, setMonth] = useState(() => startOfMonth(new Date()))

  // Keep the queried plan window centered on the viewed month.
  useEffect(() => {
    setFocusDate(month)
  }, [month, setFocusDate])

  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 })
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 })

  const days: Date[] = []
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) {
    days.push(d)
  }

  const tasksByDate = useMemo(() => {
    const map = new Map<string, typeof tasks>()
    for (const task of tasks) {
      const list = map.get(task.date) ?? []
      list.push(task)
      map.set(task.date, list)
    }
    return map
  }, [tasks])

  return (
    <section aria-label="Month calendar" className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-tight">
          {format(month, 'MMMM yyyy')}
        </h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => setMonth((m) => addMonths(m, -1))}
            aria-label="Previous month"
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMonth(startOfMonth(new Date()))}
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => setMonth((m) => addMonths(m, 1))}
            aria-label="Next month"
          >
            <ChevronRightIcon />
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border">
        <div className="grid grid-cols-7 border-b bg-muted/50">
          {weekdayHeaders().map((d) => (
            <div
              key={d}
              className="px-2 py-2 text-center text-xs font-medium tracking-wide text-muted-foreground uppercase"
            >
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day, i) => {
            const dayKey = toKey(day)
            const dayTasks = tasksByDate.get(dayKey) ?? []
            const inMonth = isSameMonth(day, month)
            const locked = isWeekLocked(startOfWeek(day, { weekStartsOn: 1 }))
            const unreadCount = dayTasks.filter(
              (t) => t.comments.length > 0 && !isCommentsRead(t),
            ).length
            const allDone =
              dayTasks.length > 0 && dayTasks.every((t) => isCompleted(t))

            return (
              <div
                key={dayKey}
                className={cn(
                  'relative flex min-h-16 flex-col gap-1.5 border-b p-2 text-left',
                  i % 7 !== 6 && 'border-r',
                  i >= days.length - 7 && 'border-b-0',
                  !inMonth && 'bg-muted/30',
                )}
              >
                <span className="flex items-center justify-between gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => onSelectDay(day)}
                        aria-label={`Open week of ${format(day, 'EEEE, MMMM d')}`}
                        className={cn(
                          'inline-flex size-6 items-center justify-center rounded-full text-xs font-medium tabular-nums transition-colors focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none',
                          isToday(day)
                            ? 'bg-primary text-primary-foreground'
                            : inMonth
                              ? 'text-foreground hover:bg-accent'
                              : 'text-muted-foreground/60 hover:bg-accent',
                        )}
                      >
                        {format(day, 'd')}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Jump to this week</TooltipContent>
                  </Tooltip>
                  <span className="flex items-center gap-1">
                    {unreadCount > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex size-4 items-center justify-center rounded-full bg-destructive text-[9px] font-semibold text-white">
                            {unreadCount}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {unreadCount} unread coach comment
                          {unreadCount === 1 ? '' : 's'}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {locked && dayTasks.length > 0 && (
                      <LockIcon
                        className="size-3 text-muted-foreground"
                        aria-hidden="true"
                      />
                    )}
                  </span>
                </span>

                {dayTasks.length > 0 && (
                  <span className="flex flex-col gap-0.5">
                    {dayTasks.map((task) => {
                      const completed = !locked && isCompleted(task)
                      return (
                        <button
                          key={task.id}
                          type="button"
                          onClick={() => openTask(task)}
                          disabled={locked}
                          aria-label={`${task.title}${completed ? ' (completed)' : ''}${locked ? ' (locked)' : ''}`}
                          className={cn(
                            '-mx-1 flex items-baseline gap-1 rounded px-1 py-px text-left text-[10px] leading-tight transition-colors focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none',
                            completed
                              ? 'text-muted-foreground/70 line-through decoration-muted-foreground/40'
                              : 'text-foreground/80',
                            locked
                              ? 'cursor-not-allowed opacity-40'
                              : 'hover:bg-accent',
                          )}
                        >
                          <span
                            className={cn(
                              'size-1.5 shrink-0 self-center rounded-full',
                              TASK_TYPE_CONFIG[task.type].dot,
                              completed && 'opacity-45',
                            )}
                            aria-hidden="true"
                          />
                          <span className="truncate">{task.title}</span>
                        </button>
                      )
                    })}
                  </span>
                )}

                {allDone && !locked && (
                  <span className="mt-auto pt-0.5 text-[10px] font-medium text-task-exercise">
                    All done
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {Object.entries(TASK_TYPE_CONFIG).map(([key, config]) => (
          <span
            key={key}
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            <span
              className={cn('size-2 rounded-full', config.dot)}
              aria-hidden="true"
            />
            {config.label}
          </span>
        ))}
      </div>
    </section>
  )
}
